import { useState } from "react";
import { Alert, Button, Col, Offcanvas, Row } from "react-bootstrap";
import { useLoaderData, useNavigate } from "react-router-dom";
import moment from "moment";
import Flight from "../../../models/flight";
import { createWbPdfBlob, wbRouteTitle } from "../../../utils/wbLoadsheet";
import { getCrewFullName } from "../../../models/crew";
import { editFlightWbLoads } from "../../../store/flightSlice";
import { useAppDispatch, useAppSelector } from "../../../store/hooks";
import { selectedOrganization } from "../../../store/organizationsSlice";
import { AviTracerApi } from "../../../avitracerApi";
import { useWbSheet } from "../../../components/wb/useWbSheet";
import WbSheetView from "../../../components/wb/WbSheetView";
import "../../../css/wb-loadsheet.css";

//The API interceptor rejects with a plain string; Redux Toolkit's unwrap() with a serialized
//{message} object — read whichever arrived.
export function errorText(e: unknown): string {
  if (typeof e === "string") return e;
  const message = (e as { message?: unknown } | undefined)?.message;
  return typeof message === "string" && message.length > 0 ? message : "unknown error";
}

function FlightWbPage() {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const loadedFlight = useLoaderData() as Flight;
  const selectedOrg = useAppSelector(selectedOrganization);

  const [saving, setSaving] = useState(false);
  const [show, setShow] = useState(true);

  const aircraft = loadedFlight.aircraft;
  const canEditWb = loadedFlight.briefing!.status === "not-started" || loadedFlight.briefing!.status === "error";

  const sheet = useWbSheet({
    flight: loadedFlight,
    initialWb: loadedFlight.wb,
    isLatArmInWbPointUp: selectedOrg?.isLatArmInWbPointUp ?? false,
  });

  // ── save ──
  const [saveError, setSaveError] = useState<string>();

  const saveAndClose = async () => {
    //a cabin that couldn't be seated is not a loading — never save it (it would read "within limits")
    if (sheet.seedError || !sheet.current) return;
    setSaving(true);
    setSaveError(undefined);
    try {
      const wb = sheet.buildWb();
      if (wb.status === "ok") {
        const pdfBlob = await createWbPdfBlob(sheet.sheetLegs.map((l) => l.input));
        await AviTracerApi.uploadWbPdf(loadedFlight.id!, pdfBlob);
      }
      await dispatch(editFlightWbLoads({ flight: loadedFlight, wb })).unwrap();
      setShow(false);
    } catch (e) {
      setSaveError("Saving failed: " + errorText(e) + " — please try again.");
    } finally {
      setSaving(false);
    }
  };

  const unit = aircraft.massUnit;

  return (
    <Offcanvas backdrop="static" show={show} onExited={() => navigate("../")} placement={"end"} className="offcanvas-size-fullscreen-lg">
      <Offcanvas.Body className="wb-sheet">
        {/* header */}
        <Row className="justify-content-between align-items-start pt-2 pb-3">
          <Col>
            <div className="wb-header">
              <span className="wb-title">{loadedFlight.flightId}</span>
              <span className="wb-sub">
                <b>{aircraft.registration}</b> · {aircraft.icaoType} · {wbRouteTitle(loadedFlight)} ·{" "}
                {moment(loadedFlight.departureDateTime).format("DD MMM YYYY")} · PIC <b>{getCrewFullName(loadedFlight.pic)}</b>
              </span>
            </div>
            <div className="wb-sub" style={{ fontSize: "0.75rem", color: "var(--wb-muted)" }}>
              Weight &amp; Balance loadsheet{sheet.legs.length > 1 ? ` · ${sheet.legs.length} legs` : ""} · weights in {unit} · arms in mm · profile “
              {aircraft.wb?.profileName}”
            </div>
          </Col>
          <Col xs={"auto"}>
            {!canEditWb ? (
              <Button variant="dark" style={{ width: "100px" }} onClick={() => setShow(false)}>
                Close
              </Button>
            ) : (
              <Button variant="dark" disabled={saving || !sheet.current || sheet.seedError !== undefined} style={{ width: "100px" }} onClick={saveAndClose}>
                {!saving ? "Save" : "Saving..."}
              </Button>
            )}
          </Col>
        </Row>

        {sheet.seedError && (
          <Alert variant="danger">
            {sheet.seedError}. The passengers don't fit the aircraft's seats — change the passengers
            {sheet.legs.length > 1 ? " or the legs they fly" : ""} in the flight before calculating the W&amp;B.
          </Alert>
        )}
        {canEditWb && (loadedFlight.wbRevisions?.length ?? 0) > 0 && (
          <Alert variant="warning">
            This flight has {loadedFlight.wbRevisions!.length} last-minute revision{loadedFlight.wbRevisions!.length > 1 ? "s" : ""} from the pilot. Saving
            the planned W&amp;B discards {loadedFlight.wbRevisions!.length > 1 ? "them" : "it"} and the no-shows.
          </Alert>
        )}
        {saveError && <Alert variant="danger">{saveError}</Alert>}

        {!sheet.seedError && <WbSheetView sheet={sheet} canEdit={canEditWb} fuelEditableFromLeg={1} />}
      </Offcanvas.Body>
    </Offcanvas>
  );
}

export default FlightWbPage;
